import { useCallback, useState } from 'react';
import { ChatMessage } from '@/models/chat.model';

interface ExtraMetadata {
    imagePreviewUrl?: string; // URL for image preview
    scratchPad?: string; // Scratch pad content
    files?: { [key: string]: { line_count: number; file_hash: string } }; // File tracking
    migration_id?: string; // Database migration ID
    file_path?: string; // File path for database operations
    db_query_result?: boolean; // Database query result status
    model_usage?: any; // Model usage statistics
    // Whether scratch pad streaming is active
    scratchPadDone?: boolean; // Whether scratch pad streaming finished
    
}

interface ExtendedChatMessage extends ChatMessage {
    extra?: ExtraMetadata;
}

export const usePmChatState = () => {
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [isChatInProgress, setIsChatInProgress] = useState<boolean>(false);

    const addUserMessage = useCallback((input: string, image?: File | null) => {
        const userMessage: ExtendedChatMessage = {
            id: Date.now() + Math.floor(Math.random() * 1000000000),
            role: 'user',
            message: input || (image ? '' : "Hi"),
            extra: {
                imagePreviewUrl: image ? URL.createObjectURL(image) : undefined,
            }
        };
        //check if there is a loading message, if yes, update it with the user message

        setMessages(prevMessages => {
            const tempMessages = [...prevMessages];
            if(tempMessages?.length > 0 && tempMessages[tempMessages.length - 1].role === 'loading'){
                tempMessages[tempMessages.length - 1] = {
                    ...tempMessages[tempMessages.length - 1],
                    role: 'agent',
                }
            }
            // Return tempMessages with the modified loading->agent message plus the new user message
            return [...tempMessages, userMessage];
        });
    }, []);

    const addLoadingMessage = useCallback(() => {
        const loadingMessage: ExtendedChatMessage = {
            id: Date.now() + Math.floor(Math.random() * 1000000000),
            role: 'loading',
            message: '',
        };
        setMessages(prevMessages => [...prevMessages, loadingMessage]);
    }, []);

    const updateLoadingMessage = useCallback((message: Partial<ExtendedChatMessage>, isStreaming?: boolean) => {
        setMessages(prevMessages => {
            const tempMessages = [...prevMessages];
            const lastMessage = tempMessages[tempMessages.length - 1];
            
            if (lastMessage && lastMessage.role === 'loading') {
                // Update the loading message with new content
                tempMessages[tempMessages.length - 1] = {
                    ...lastMessage,
                    message: (lastMessage.message + message.message),
                    extra: {
                        ...lastMessage.extra,
                        ...message.extra,
                    },
                    role: isStreaming ? 'loading' : 'agent',
                };
            } else if (isStreaming) {
                // If no loading message exists but we're streaming, create one
                const newLoadingMessage: ExtendedChatMessage = {
                    id: Date.now() + Math.floor(Math.random() * 1000000000),
                    role: 'loading',
                    message: message.message || '',
                    extra: message.extra,
                };
                tempMessages.push(newLoadingMessage);
            }
            
            return tempMessages;
        });
    }, []);

    const handleErrorMessage = useCallback((errorMessage?: string) => {
        setMessages(prevMessages => {
            const tempMessages = [...prevMessages];
            const lastMessage = tempMessages[tempMessages.length - 1];
            
            if (lastMessage && lastMessage.role === 'loading') {
                // Replace loading message with error message
                tempMessages[tempMessages.length - 1] = {
                    ...lastMessage,
                    role: 'agent',
                    message: errorMessage || 'An error occurred. Please try again.',
                };
            } else {
                // Add error message if no loading message exists
                const errorMsg: ExtendedChatMessage = {
                    id: Date.now() + Math.floor(Math.random() * 1000000000),
                    role: 'agent',
                    message: errorMessage || 'An error occurred. Please try again.',
                };
                tempMessages.push(errorMsg);
            }
            
            return tempMessages;
        });
    }, []);

    const setInitialMessages = useCallback((initialMessages: ExtendedChatMessage[]) => {
        setMessages(initialMessages);
    }, []);

    const clearMessages = useCallback(() => {
        setMessages([]);
    }, []);

    const addPmAgentMessage = useCallback((message: string, extra?: ExtraMetadata) => {
        const agentMessage: ExtendedChatMessage = {
            id: Date.now() + Math.floor(Math.random() * 1000000000),
            role: 'agent',
            message,
            extra,
        };
        setMessages(prevMessages => [...prevMessages, agentMessage]);
    }, []);

    return {
        messages,
        isChatInProgress,
        setIsChatInProgress,
        addUserMessage,
        addLoadingMessage,
        updateLoadingMessage,
        handleErrorMessage,
        setInitialMessages,
        clearMessages,
        addPmAgentMessage,
    };
};
